import { supabase } from "@core/lib/supabase";
import { logger } from "../lib/logger";

export interface FormFunnelMetrics {
  anonymous: { current: number; previous: number; deltaPct: number };
  signedUp: { total: number; new30d: number };
  active: { count: number; pctOfSignedUp: number };
  paying: { count: number; new30d: number; churn30d: number; mrrEur: number };
  conversion: {
    anonToSignup: number;
    signupToActive: number;
    activeToPaying: number;
  };
  generatedAt: string;
}

export interface FormTimeSeriesPoint {
  date: string;
  anonymous: number;
  signed_up: number;
  active: number;
  paying: number;
}

export type FormPulseEventType =
  | "new_paying"
  | "churn"
  | "engaged_anon"
  | "first_poll";

export interface FormPulseEvent {
  type: FormPulseEventType;
  at: string;
  label: string;
  meta: Record<string, unknown>;
}

export interface FormCohortRetentionPoint {
  q_offset: number;
  retained: number;
}

export interface FormQuarterlyCohort {
  cohort_quarter: string;
  size: number;
  retention: FormCohortRetentionPoint[];
}

export interface FormCohortMatrix {
  max_offset: number;
  cohorts: FormQuarterlyCohort[];
}

interface FormFunnelRpcRow {
  anonymous: number;
  anonymous_previous: number;
  signed_up_total: number;
  signed_up_new: number;
  active: number;
  paying_current: number;
  paying_new: number;
  paying_churn: number;
  mrr_eur: number;
  days: number;
  generated_at: string;
}

const pct = (num: number, denom: number): number =>
  denom > 0 ? Math.round((num / denom) * 1000) / 10 : 0;

export class FormPulseMetricsService {
  private static instance: FormPulseMetricsService;

  public static getInstance(): FormPulseMetricsService {
    if (!FormPulseMetricsService.instance) {
      FormPulseMetricsService.instance = new FormPulseMetricsService();
    }
    return FormPulseMetricsService.instance;
  }

  async getFunnel(days: number = 30): Promise<FormFunnelMetrics> {
    const { data, error } = await supabase.rpc("get_form_pulse_funnel", {
      days,
    });
    if (error) {
      logger.error(
        "[FormPulseMetricsService] get_form_pulse_funnel failed",
        error,
      );
      throw error;
    }
    const row = data as FormFunnelRpcRow;

    const anonDelta =
      row.anonymous_previous > 0
        ? Math.round(
            ((row.anonymous - row.anonymous_previous) /
              row.anonymous_previous) *
              1000,
          ) / 10
        : 0;

    return {
      anonymous: {
        current: row.anonymous,
        previous: row.anonymous_previous,
        deltaPct: anonDelta,
      },
      signedUp: {
        total: row.signed_up_total,
        new30d: row.signed_up_new,
      },
      active: {
        count: row.active,
        pctOfSignedUp: pct(row.active, row.signed_up_total),
      },
      paying: {
        count: row.paying_current,
        new30d: row.paying_new,
        churn30d: row.paying_churn,
        mrrEur: Number(row.mrr_eur) || 0,
      },
      conversion: {
        anonToSignup: pct(row.signed_up_new, row.anonymous),
        signupToActive: pct(row.active, row.signed_up_total),
        activeToPaying: pct(row.paying_current, row.active),
      },
      generatedAt: row.generated_at,
    };
  }

  async getTimeSeries(days: number = 90): Promise<FormTimeSeriesPoint[]> {
    const { data, error } = await supabase.rpc("get_form_pulse_timeseries", {
      days,
    });
    if (error) {
      logger.error(
        "[FormPulseMetricsService] get_form_pulse_timeseries failed",
        error,
      );
      throw error;
    }
    return (data ?? []) as FormTimeSeriesPoint[];
  }

  async getQuarterlyCohorts(maxOffset = 4): Promise<FormCohortMatrix> {
    const { data, error } = await supabase.rpc(
      "get_form_pulse_cohort_quarterly",
      { max_offset: maxOffset },
    );
    if (error) {
      logger.error(
        "[FormPulseMetricsService] get_form_pulse_cohort_quarterly failed",
        error,
      );
      throw error;
    }
    return (data ?? { max_offset: maxOffset, cohorts: [] }) as FormCohortMatrix;
  }

  async getRecentEvents(limit = 20): Promise<FormPulseEvent[]> {
    const { data, error } = await supabase.rpc("get_form_pulse_events", {
      limit_count: limit,
    });
    if (error) {
      logger.error(
        "[FormPulseMetricsService] get_form_pulse_events failed",
        error,
      );
      throw error;
    }
    return (data ?? []) as FormPulseEvent[];
  }
}

export const formPulseMetricsService = FormPulseMetricsService.getInstance();
